export type AccountingScopeKind = 'ESTABLISHMENT' | 'GROUP'

export type AccountingExpenseCategory =
  | 'SALARIES'
  | 'UTILITIES'
  | 'TRANSPORT'
  | 'MAINTENANCE'
  | 'SCHOOL_SUPPLIES'
  | 'RENT'
  | 'EQUIPMENT'
  | 'TAXES'
  | 'INSURANCE'
  | 'OTHER'

export type AccountingExpenseStatus =
  | 'DRAFT'
  | 'VALIDATED'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'CANCELLED'

export const ACCOUNTING_EXPENSE_VISIBILITIES = ['ACTIVE', 'CANCELLED', 'DELETED', 'ALL'] as const
export type AccountingExpenseVisibility = (typeof ACCOUNTING_EXPENSE_VISIBILITIES)[number]
export const DEFAULT_ACCOUNTING_EXPENSE_VISIBILITY: AccountingExpenseVisibility = 'ACTIVE'
export const ACCOUNTING_ACTIVE_EXPENSE_STATUSES: AccountingExpenseStatus[] = [
  'DRAFT',
  'VALIDATED',
  'PARTIALLY_PAID',
  'PAID',
]

export type AccountingDocumentType = 'FACTURE' | 'BL' | 'OTHER'
export const ACCOUNTING_SUPPLIER_EMAIL_AVAILABILITIES = [
  'AVAILABLE',
  'NOT_AVAILABLE',
  'UNKNOWN',
] as const
export type AccountingSupplierEmailAvailability =
  (typeof ACCOUNTING_SUPPLIER_EMAIL_AVAILABILITIES)[number]
export type AccountingInvoiceStatus = 'RECEIVED' | 'MISSING' | 'PENDING_VERIFICATION' | 'NOT_REQUIRED'
export type AccountingWithholdingReviewStatus = 'NEEDS_REVIEW' | 'APPLICABLE' | 'NOT_APPLICABLE'
export type AccountingTejStatus =
  | 'NOT_STARTED'
  | 'PENDING'
  | 'DECLARED'
  | 'CERTIFICATE_RECEIVED'
  | 'NOT_REQUIRED'

export const ACCOUNTING_DOCUMENT_TYPES: AccountingDocumentType[] = ['FACTURE', 'BL', 'OTHER']
export const ACCOUNTING_FOLLOWUP_STATUSES = ['OPEN', 'CONTACTED', 'OVERDUE', 'RESOLVED'] as const
export type AccountingFollowUpStatus = (typeof ACCOUNTING_FOLLOWUP_STATUSES)[number]
export const ACCOUNTING_WITHHOLDING_RATES = [0, 0.5, 1, 1.5] as const
export const ACCOUNTING_TEJ_STATUSES: AccountingTejStatus[] = [
  'NOT_STARTED',
  'PENDING',
  'DECLARED',
  'CERTIFICATE_RECEIVED',
  'NOT_REQUIRED',
]

export type AccountingTicketPrintOutcome = 'REQUESTED' | 'CONFIRMED' | 'FAILED' | 'UNKNOWN'
export type AccountingPrintHistoryStatus = 'TRACKED' | 'UNKNOWN'

export const ACCOUNTING_EXPENSE_CATEGORIES: AccountingExpenseCategory[] = [
  'SALARIES',
  'UTILITIES',
  'TRANSPORT',
  'MAINTENANCE',
  'SCHOOL_SUPPLIES',
  'RENT',
  'EQUIPMENT',
  'TAXES',
  'INSURANCE',
  'OTHER',
]

export const ACCOUNTING_REPORT_TYPES = [
  'daily',
  'monthly',
  'academic-year',
  'establishment',
  'group',
  'payment-journal',
  'expense-journal',
  'ticket-print',
  'outstanding',
  'cashflow',
] as const

export type AccountingReportType = (typeof ACCOUNTING_REPORT_TYPES)[number]
export type AccountingReportFormat = 'json' | 'csv' | 'html' | 'pdf'

export interface AccountingScopeQuery {
  scope?: AccountingScopeKind
  establishmentId?: string
  academicYear?: string
  from?: string
  to?: string
  paymentMethod?: string
}

export type AccountingAttachmentKind = 'INVOICE' | 'BL' | 'TEJ' | 'OTHER'

export const ACCOUNTING_ATTACHMENT_KINDS: AccountingAttachmentKind[] = [
  'INVOICE',
  'BL',
  'TEJ',
  'OTHER',
]

export interface AccountingListQuery extends AccountingScopeQuery {
  page?: number
  limit?: number
  search?: string
  ticketNumber?: string
  sort?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc' | string
  status?: string
  category?: string
  documentType?: string
  invoiceStatus?: string
  missingInvoice?: boolean | string
  withholdingReviewStatus?: string
  tejStatus?: string
  supplierName?: string
  supplierInvoiceNumber?: string
  deliveryNoteNumber?: string
  internalReference?: string
  minAmountTtc?: number | string
  maxAmountTtc?: number | string
  visibility?: AccountingExpenseVisibility | string
  excludeStatus?: string
  excludeExpenseStatus?: string
  includeCancelledExpenses?: boolean
  includeDeleted?: boolean | string
  deletedOnly?: boolean | string
  paymentType?: string
  action?: string
  entityType?: string
}

export interface AccountingPagination {
  page: number
  limit: number
  total: number
  pages: number
}

export const ACCOUNTING_FOLLOWUP_PAGE_SIZES = [10, 20, 50] as const
export const ACCOUNTING_FOLLOWUP_SORT_FIELDS = [
  'nextFollowUpAt',
  'expenseDate',
  'supplierName',
  'status',
] as const
export type AccountingFollowUpSortField = (typeof ACCOUNTING_FOLLOWUP_SORT_FIELDS)[number]

export interface AccountingInvoiceFollowUpStats {
  missingInvoices?: number
  overdue?: number
  contacted?: number
  resolved?: number
}

export interface AccountingInvoiceFollowUpPagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface AccountingPaymentUser {
  _id?: string
  firstName?: string
  lastName?: string
  email?: string
}

export interface AccountingPaymentClassroom {
  _id?: string
  name?: string
}

export interface AccountingPaymentRow {
  id: string
  ticketNumber?: string | null
  establishmentId?: string | null
  establishmentName?: string | null
  classroom?: AccountingPaymentClassroom | null
  paymentDate?: string | null
  paymentType?: string | null
  paymentMethod?: string | null
  paymentTotal?: number
  status?: string | null
  isRegisteredUser?: boolean
  guestPayerName?: string | null
  user?: AccountingPaymentUser | null
}

export interface AccountingTicketRow extends AccountingPaymentRow {
  printHistoryStatus?: AccountingPrintHistoryStatus
  reprintCount?: number
  confirmedPrints?: number
}

export interface AccountingPrintEvent {
  _id?: string
  id?: string
  ticket?: string
  payment?: string
  outcome?: AccountingTicketPrintOutcome
  isReprint?: boolean
  printedAt?: string
  requestedAt?: string
  confirmedAt?: string
  idempotencyKey?: string
  createdAt?: string
  note?: string
}

export interface AccountingExpensePayment {
  id?: string
  _id?: string
  amount?: number
  amountMills?: number
  paymentMethod?: string | null
  paidAt?: string
  createdAt?: string
}

export interface AccountingExpenseAttachment {
  id?: string
  _id?: string
  fileName?: string
  originalName?: string
  mimeType?: string
  size?: number
  kind?: AccountingAttachmentKind | string
  archived?: boolean
  isArchived?: boolean
  isActive?: boolean
  uploadedAt?: string
  archivedAt?: string
}

export interface AccountingWithholding {
  reviewStatus?: AccountingWithholdingReviewStatus | string
  applicable?: boolean | null
  rate?: number | null
  taxBaseMillimes?: number | null
  amountMillimes?: number | null
  reviewedBy?: string | null
  reviewedAt?: string | null
  reason?: string
  thresholdTriggered?: boolean
}

export interface AccountingTej {
  status?: AccountingTejStatus | string
  certificateReference?: string | null
  declaredAt?: string | null
  certificateAttachment?: unknown
  tejPlatformSubmitted?: boolean
  notes?: string
}

export interface AccountingExpense {
  id: string
  reference?: string
  establishmentId?: string | null
  establishmentName?: string | null
  expenseDate?: string
  category?: AccountingExpenseCategory | string
  amountHt?: number
  vatAmount?: number
  totalTtc?: number
  amountTtc?: number
  amountHtMills?: number
  vatAmountMills?: number
  totalTtcMills?: number
  paid?: number
  outstanding?: number
  paidMills?: number
  outstandingMills?: number
  withholdingAmount?: number
  netPayableSupplier?: number
  paidSupplierAmount?: number
  remainingSupplierAmount?: number
  beneficiary?: string
  supplier?: string
  supplierName?: string
  supplierTaxId?: string
  supplierEmail?: string
  supplierEmailAvailability?: AccountingSupplierEmailAvailability | string
  description?: string
  invoiceNumber?: string
  documentType?: AccountingDocumentType | string | null
  supplierInvoiceNumber?: string
  supplierInvoiceDate?: string | null
  deliveryNoteNumber?: string
  invoiceStatus?: AccountingInvoiceStatus | string | null
  invoiceReceivedAt?: string | null
  missingInvoiceJustification?: string
  attachments?: AccountingExpenseAttachment[]
  withholding?: AccountingWithholding
  tej?: AccountingTej
  paymentMethod?: string | null
  status?: AccountingExpenseStatus | string
  validatedAt?: string | null
  cancelledAt?: string | null
  cancelReason?: string | null
  payments?: AccountingExpensePayment[]
}

export interface AccountingCreateExpensePayload {
  establishmentId: string
  expenseDate: string
  category: AccountingExpenseCategory
  amountHt: number
  vatAmount?: number
  beneficiary?: string
  supplier?: string
  supplierName?: string
  supplierTaxId?: string
  supplierEmail?: string
  supplierEmailAvailability?: AccountingSupplierEmailAvailability
  paymentMethod?: string
  description?: string
  documentType?: AccountingDocumentType
  supplierInvoiceNumber?: string
  supplierInvoiceDate?: string
  deliveryNoteNumber?: string
  missingInvoiceJustification?: string
  idempotencyKey?: string
  file?: File
  invoice?: File
  bl?: File
}

export interface AccountingUpdateExpensePayload {
  expenseDate?: string
  category?: AccountingExpenseCategory
  amountHt?: number
  vatAmount?: number
  beneficiary?: string
  supplier?: string
  supplierName?: string
  supplierTaxId?: string
  supplierEmail?: string
  supplierEmailAvailability?: AccountingSupplierEmailAvailability
  paymentMethod?: string
  description?: string
  documentType?: AccountingDocumentType
  supplierInvoiceNumber?: string
  supplierInvoiceDate?: string
  deliveryNoteNumber?: string
  missingInvoiceJustification?: string
}

export interface AccountingAttachInvoicePayload {
  supplierInvoiceNumber: string
  supplierInvoiceDate?: string
  supplierName?: string
  supplierTaxId?: string
}

export interface AccountingWithholdingReviewPayload {
  reviewStatus: AccountingWithholdingReviewStatus
  rate?: number
  taxBase?: 'TTC' | 'HT'
  reason?: string
}

export interface AccountingTejPatchPayload {
  status: AccountingTejStatus
  certificateReference?: string
  declaredAt?: string
  notes?: string
}

export interface AccountingFollowUpContact {
  contactedAt?: string
  channel?: string
  note?: string
  nextFollowUpAt?: string
  createdBy?: string
}

export interface AccountingSupplierLookupQuery {
  supplierName: string
  establishmentId?: string
  scope?: AccountingScopeKind
}

export interface AccountingSupplier {
  id?: string
  supplierName?: string
  supplierTaxId?: string
  supplierEmail?: string
  supplierEmailAvailability?: AccountingSupplierEmailAvailability | string
}

export type AccountingSupplierLookupItem = AccountingSupplier

export interface AccountingEmailReminderEstablishment {
  name?: string
  fiscalName?: string
  taxId?: string
  phone?: string
}

export interface AccountingEmailReminderDraft {
  recipient?: string
  cc?: string[]
  subject?: string
  body?: string
  previewNotice?: string
  signatureLines?: string[]
  establishment?: AccountingEmailReminderEstablishment
  deliveryNoteNumber?: string
  expenseReference?: string
}

export interface AccountingEmailReminderSendPayload {
  subject: string
  body: string
  cc?: string[]
  to?: string
}

export type AccountingEmailReminderSendStatus =
  | 'QUEUED'
  | 'SENT'
  | 'FAILED'
  | 'SEND_IN_PROGRESS'
  | 'MANUAL_CONTACT'

export interface AccountingEmailReminderSendResult {
  status?: string
  recipient?: string
  subject?: string
  body?: string
  reminderId?: string
  sent?: boolean
  queued?: boolean
  contacted?: boolean
  inProgress?: boolean
  duplicate?: boolean
}

export interface AccountingFollowUpReminder {
  id?: string
  createdAt?: string
  channel?: string
  recipient?: string
  status?: string
  actor?: string
  note?: string
  subject?: string
  body?: string
  cc?: string[]
  nextFollowUpAt?: string
}

export interface AccountingManualReminderPayload {
  channel: 'PHONE' | 'OTHER'
  note?: string
  nextFollowUpAt?: string
}

export interface AccountingInvoiceFollowUp {
  id: string
  expenseId?: string
  establishmentId?: string
  supplierName?: string
  supplierEmail?: string
  supplierEmailAvailability?: AccountingSupplierEmailAvailability | string
  deliveryNoteNumber?: string
  expenseReference?: string
  reference?: string
  expenseDate?: string
  amountTtc?: number | string | null
  amountTtcMills?: number | string | null
  totalTtc?: number
  invoiceStatus?: AccountingInvoiceStatus | string | null
  resolutionReason?: string | null
  expenseMissing?: boolean
  status?: string
  createdAt?: string
  lastContactAt?: string | null
  nextFollowUpAt?: string | null
  contactHistory?: AccountingFollowUpContact[]
  resolvedAt?: string | null
  isActive?: boolean
  expenseStatus?: string
  expense?: AccountingExpense
}

export interface AccountingContactFollowUpPayload {
  note: string
  channel?: string
  contactedAt?: string
  nextFollowUpAt?: string
}

export interface AccountingPayExpensePayload {
  amount: number
  idempotencyKey: string
  paymentMethod?: string
  paidAt?: string
}

export interface AccountingCancelExpensePayload {
  reason: string
  confirmReversal?: boolean
}

export interface AccountingPrintEventPayload {
  outcome: AccountingTicketPrintOutcome
  idempotencyKey: string
  printerId?: string
  note?: string
}

export interface AccountingLabeledTotal {
  paymentMethod?: string
  establishmentId?: string | null
  category?: string
  status?: string
  total?: number
  totalTtc?: number
  paid?: number
  count?: number
}

export interface AccountingMoneyBucket {
  totalReceived?: number
  count?: number
  unscopedCount?: number
  byPaymentType?: AccountingLabeledTotal[]
  byPaymentMethod?: AccountingLabeledTotal[] | Record<string, number>
  byEstablishment?: AccountingLabeledTotal[] | Record<string, number>
}

export interface AccountingDepensesBucket {
  recordedTtc?: number
  recordedCount?: number
  validatedTtc?: number
  paidTtc?: number
  outstandingTtc?: number
  cancelledTtc?: number
  byStatus?: Record<string, {count?: number; totalTtc?: number; paid?: number}>
  byCategory?: Record<string, number>
  byEstablishment?: Record<string, number>
}

export interface AccountingTresorerieBucket {
  netCashMovement?: number
  openingClosingAvailable?: boolean
}

export interface AccountingTicketsBucket {
  issued?: number
  confirmedPrints?: number
  reprints?: number
  unknownHistory?: number
  historicalUnknownPrints?: number
  printRequests?: number
}

export interface AccountingUnallocatedReceipts {
  total?: number
  count?: number
}

export interface AccountingImpayesBucket {
  total?: number
  count?: number
  overdue?: number
  notYetDue?: number
  overdueStudentCount?: number
  unallocatedReceipts?: AccountingUnallocatedReceipts | number
}

export type AccountingOverdueStatus = 'overdue' | 'upcoming' | 'current' | 'unknown'

export interface AccountingOutstandingRow {
  id: string
  studentName: string
  establishmentName: string
  classroomName: string
  academicYear: string
  paymentCategory: string
  billingPeriod: string
  dueDate: string
  amountDue?: number
  allocatedPayments?: number
  remaining?: number
  overdueStatus: AccountingOverdueStatus
}

export interface AccountingOutstandingTotals {
  amountDue?: number
  allocatedPayments?: number
  remaining?: number
}

export interface AccountingOutstandingReportView {
  rows: AccountingOutstandingRow[]
  paginated: boolean
  totals: AccountingOutstandingTotals
  summary?: AccountingSummary
}

export interface AccountingAvailability {
  cashOpeningClosingBalances?: boolean
}

export interface AccountingPeriod {
  academicYear?: string
  from?: string
  to?: string
}

export interface AccountingSupplierAccounting {
  totalExpensesTtc?: number
  totalSupplierNetPayable?: number
  totalSupplierPayments?: number
  outstandingSupplierBalances?: number
  missingSupplierInvoices?: number
  openBlFollowUps?: number
  overdueInvoiceFollowUps?: number
  expensesNeedingWithholdingReview?: number
  pendingTejCertificates?: number
  withholdingAmountsPendingRemittance?: number
}

export interface AccountingSummary {
  module?: string
  isolatedFromZad?: boolean
  scope?: AccountingScopeKind
  establishmentIds?: string[]
  period?: AccountingPeriod
  availability?: AccountingAvailability
  encaissements?: AccountingMoneyBucket
  depenses?: AccountingDepensesBucket
  tresorerie?: AccountingTresorerieBucket
  tickets?: AccountingTicketsBucket
  impayes?: AccountingImpayesBucket
  supplierAccounting?: AccountingSupplierAccounting
}

export interface AccountingAuditRow {
  id?: string
  _id?: string
  createdAt?: string
  action?: string
  entityType?: string
  entityId?: string
  establishmentId?: string | null
  establishmentName?: string | null
  establishement?: unknown
  actor?: {
    _id?: string
    firstName?: string
    lastName?: string
    email?: string
  } | string | null
  payload?: Record<string, unknown>
}

export interface AccountingReportEnvelope {
  type?: string
  format?: string
  generatedAt?: string
  scope?: AccountingScopeKind
  establishmentIds?: string[]
  period?: AccountingPeriod
  rows?: unknown[]
  outstanding?: unknown[]
  charges?: unknown[]
  studentCharges?: unknown[]
  items?: unknown[]
  data?: unknown
  totals?: Record<string, unknown>
  summary?: AccountingSummary
  totalsAlignedWithDashboard?: boolean
  payments?: unknown[]
  expenses?: unknown[]
  tickets?: unknown[]
  page?: number
  limit?: number
  total?: number
  totalPages?: number
  pagination?: {
    page?: number
    limit?: number
    total?: number
    totalPages?: number
    hasMore?: boolean
    nextPage?: number | null
  }
}
